import React, { createContext, useContext, useMemo } from 'react';

const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ user, children }) => {
  const value = useMemo(() => {
    if (!user) {
      return {
        hasPermission: () => false,
        hasModuleAccess: () => false,
        isAdmin: false,
        isSuperAdmin: false,
        moduleAccess: [],
        permissions: []
      };
    }

    const isSuperAdmin = user.role === 'super_admin';
    const isAdmin = user.role === 'admin' || isSuperAdmin;
    const moduleAccess = user.module_access || user.modules || [];
    const permissions = user.permissions || [];
    const deniedPermissions = user.denied_permissions || [];

    const hasPermission = (permission) => {
      if (isSuperAdmin) return true;
      if (permissions.includes('*')) return true;
      if (deniedPermissions.includes(permission)) return false;
      return permissions.includes(permission);
    };

    const hasModuleAccess = (module) => {
      if (isSuperAdmin) return true;
      return moduleAccess.includes(module);
    };

    const hasAnyPermission = (permissionList) => {
      return permissionList.some(p => hasPermission(p));
    };

    const hasAllPermissions = (permissionList) => {
      return permissionList.every(p => hasPermission(p));
    };

    return {
      hasPermission,
      hasModuleAccess,
      hasAnyPermission,
      hasAllPermissions,
      isAdmin,
      isSuperAdmin,
      moduleAccess,
      permissions,
      user
    };
  }, [user]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

// Permission constants for easy reference
export const PERMISSIONS = {
  PROCUREMENT: {
    VIEW: 'procurement.view',
    CREATE: 'procurement.create',
    EDIT_OWN: 'procurement.edit_own',
    EDIT_ALL: 'procurement.edit_all',
    DELETE: 'procurement.delete',
    APPROVE: 'procurement.approve',
    MANAGE_VENDORS: 'procurement.manage_vendors',
    MANAGE_BUDGETS: 'procurement.manage_budgets',
    EXPORT: 'procurement.export',
    ANALYTICS: 'procurement.analytics'
  },
  TIMESHEETS: {
    VIEW: 'timesheets.view',
    SUBMIT: 'timesheets.submit',
    EDIT_OWN: 'timesheets.edit_own',
    APPROVE: 'timesheets.approve',
    VIEW_ALL: 'timesheets.view_all',
    EXPORT: 'timesheets.export'
  },
  ONBOARDING: {
    VIEW: 'onboarding.view',
    CREATE: 'onboarding.create',
    EDIT: 'onboarding.edit',
    DELETE: 'onboarding.delete',
    MANAGE_CONTRACTS: 'onboarding.manage_contracts',
    EXPORT: 'onboarding.export'
  },
  TRANSPORTATION: {
    VIEW: 'transportation.view',
    CREATE: 'transportation.create',
    EDIT: 'transportation.edit',
    DELETE: 'transportation.delete',
    MANAGE_DRIVERS: 'transportation.manage_drivers',
    ASSIGN_BOOKINGS: 'transportation.assign_bookings',
    EXPORT: 'transportation.export'
  },
  FACILITIES: {
    VIEW: 'facilities.view',
    CREATE: 'facilities.create',
    EDIT: 'facilities.edit',
    DELETE: 'facilities.delete',
    ASSIGN_WORK_ORDERS: 'facilities.assign_work_orders',
    MANAGE_ASSETS: 'facilities.manage_assets',
    EXPORT: 'facilities.export'
  },
  EVENTS: {
    VIEW: 'events.view',
    CREATE: 'events.create',
    EDIT: 'events.edit',
    DELETE: 'events.delete',
    MANAGE_PARTICIPANTS: 'events.manage_participants',
    MANAGE_INVOICES: 'events.manage_invoices',
    MANAGE_ROOMS: 'events.manage_rooms',
    SEND_COMMUNICATIONS: 'events.send_communications',
    EXPORT: 'events.export'
  },
  USER_MANAGEMENT: {
    VIEW: 'user_management.view',
    CREATE: 'user_management.create',
    EDIT: 'user_management.edit',
    DELETE: 'user_management.delete',
    APPROVE_USERS: 'user_management.approve_users',
    MANAGE_PERMISSIONS: 'user_management.manage_permissions'
  }
};

export default PermissionsContext;
